import classNames from 'classnames';
import React, { useRef, useState } from 'react';
import { Message } from '@meitu/saas-ui';
import {
    getOperateDirection,
    getOperateType,
    useFramework,
    useIsMouseSelect,
    useIsPickStage,
    useMoveEventListener,
} from '@/framework';
import { useLocaleStore } from '@/i18n/index';
import { useResizeCursor } from '@/utils/cursor-kit';
import { ITouchKitEvent } from '@/utils/event-kit';
import { parseToUnit } from '@/utils/unit-kit';
import { BaseLayerViewType, MAX_STAGE_HEIGHT, MIN_STAGE_HEIGHT, ResizeOperateType } from '@/version-types';
import { type QuickResizeOperate } from './operate/quick-resize-operate';
import { StageOperateImpl } from './stage-operate';

/**
 * 长画布的舞台调整尺寸
 */
export const ResizeOperate: React.FC = React.memo(() => {
    const { BC } = useResizeCursor(0);
    const { $t } = useLocaleStore();
    const framework = useFramework();
    const isPickStage = useIsPickStage();
    const tipDefaultTxt = $t((v) => v.stageManager.dragHeight);
    const [tipTxt, setTipTxt] = useState(tipDefaultTxt);
    const [operating, setOperating] = useState(false);
    const originHeight = useRef<number>(1);
    const newHeight = useRef<number>(1);
    const adjustOriginIndex = useRef(0);
    const isMouseSelect = useIsMouseSelect();
    const stageOperate = framework.service.registration.getInstance<StageOperateImpl>('StageOperate');

    useMoveEventListener((e: ITouchKitEvent) => {
        const target = e.target as HTMLElement;
        const operateType = getOperateType(target);

        if (operateType !== BaseLayerViewType.STAGE_RESIZE) return;
        const direction = getOperateDirection(target);
        if (!direction) return;

        if (e.isStart) {
            adjustOriginIndex.current = framework.service.frame.getActiveFrameId();
            const bg = framework.service.frame.getBackgroundLayer(adjustOriginIndex.current);
            if (!bg) return;
            originHeight.current = bg.height;
            setOperating(true);
            framework.service.editor.setDisablePick(true);
            document.body.classList.add(BC);

            // 发送舞台尺寸变化前事件
            stageOperate.emitResizeStart();
        }
        const offset = e.displacementY / framework.service.edgelessCanvas.zoom;
        let newHeightTmp = Math.max(MIN_STAGE_HEIGHT, Math.floor(originHeight.current + offset));
        if (newHeightTmp > MAX_STAGE_HEIGHT) {
            Message.error($t((v) => v.stageManager.canvasLimitScope));
            newHeightTmp = MAX_STAGE_HEIGHT;
        }
        newHeight.current = newHeightTmp;

        framework.service.registration
            .getInstance<QuickResizeOperate>('QuickResizeOperate')
            .doResize(adjustOriginIndex.current, newHeight.current)
            .send(adjustOriginIndex.current, originHeight.current, newHeight.current, e.isStart, e.isEnd);

        const unit = framework.service.editor.getUnit();
        setTipTxt(`${parseToUnit(newHeight.current, unit)}${unit}`);

        if (e.isEnd) {
            framework.service.editor.setDisablePick(false);
            setTipTxt(tipDefaultTxt);
            setOperating(false);
            framework.service.registration
                .getInstance<QuickResizeOperate>('QuickResizeOperate')
                .write(adjustOriginIndex.current, originHeight.current, newHeight.current);
            document.body.classList.remove(BC);

            // 发送舞台尺寸变化后事件
            stageOperate.emitResizeEnd();
        }

        // 发送舞台尺寸调整事件
        if (!e.isEnd && !e.isStart) {
            // 通知上层业务插件同步处理舞台尺寸变化
            stageOperate.emitResizing({
                newSize: newHeight.current,
                oldSize: originHeight.current,
                operateType: ResizeOperateType.BC,
                stageIndex: framework.service.frame.frameIdToIndex(adjustOriginIndex.current),
            });
        }
    });

    return (
        <>
            {isPickStage ? <div className="c-quick-stage-resize-border"></div> : null}
            <div
                className={classNames('c-quick-stage-resize-control', {
                    active: operating,
                    disable: isMouseSelect,
                })}
            >
                <div
                    data-operate-type={BaseLayerViewType.STAGE_RESIZE}
                    data-direction={ResizeOperateType.BC}
                    className={classNames('c-quick-stage-resize-area', BC, {
                        active: isPickStage,
                    })}
                    onMouseDown={() => {
                        framework.service.frame.setPickActiveFrame(true);
                    }}
                >
                    <div className="c-quick-stage-resize-area-bar"></div>
                </div>
                <div className="c-quick-stage-resize-tip">{tipTxt}</div>
            </div>
        </>
    );
});
